<template>
  <div style="display: none"></div>
</template>

<script>
import pdfMake from 'pdfmake/build/pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts'
import { getFullAddress } from '@/utils/common'

// Initialize pdfmake fonts safely
pdfMake.vfs = pdfFonts && pdfFonts.pdfMake ? pdfFonts.pdfMake.vfs : pdfFonts

export default {
  name: 'CaseLedgerPDFMake',
  props: {
    selectedCase: Object,
    ledgerEntries: Array,
    orgName: String,
    branchName: {
      type: String,
      default: ''
    }
  },
  computed: {
    currentDateTime() {
      return this.$dayjs().format('DD-MM-YYYY hh:mm:ss a')
    },
    totalDebit() {
      return this.ledgerEntries.reduce((sum, e) => sum + (e.debit || 0), 0)
    },
    totalCredit() {
      return this.ledgerEntries.reduce((sum, e) => sum + (e.credit || 0), 0)
    },
    finalBalance() {
      return Math.abs(this.totalDebit - this.totalCredit)
    },
    borrowerAddress() {
      const customer = this.selectedCase?.customer
      if (!customer) return 'N/A'
      const addr = customer.customerAddress && customer.customerAddress.length > 0
        ? customer.customerAddress[0]
        : customer
      return getFullAddress(addr)
    },
    borrowerMobile() {
      const customer = this.selectedCase?.customer
      return customer?.customerPhoneNumber || (Array.isArray(customer?.customerMobile) ? customer.customerMobile[0] : 'N/A')
    },
    guarantors() {
      return (this.selectedCase?.guarantors && this.selectedCase.guarantors.length > 0
        ? this.selectedCase.guarantors
        : this.selectedCase?.loanDetails?.guarantors) || []
    },
    computedOrgName() {
      const orgData = this.$storage.getUniversal('orgData')
      return orgData?.name || this.orgName || 'ZAVRON FINANCE PVT LTD'
    },
    computedOrgAddressText() {
      const orgData = this.$storage.getUniversal('orgData')
      const orgAddr = orgData?.organization_address || {}
      
      const orgAddrParts = [
        orgAddr.address_line_1,
        orgAddr.address_line_2,
        orgAddr.city,
        orgAddr.state,
        orgAddr.pincode
      ].filter(Boolean).join(', ')

      const regOffice = orgAddrParts ? `Registered Office: ${orgAddrParts}` : 'Registered Office: -'

      const branchVal = this.selectedCase?.branch
      let branchAddressStr = ''

      if (branchVal && typeof branchVal === 'object') {
        const branchName = branchVal.branchName || branchVal.name || ''
        const loc = branchVal.branchLocation || {}
        const street = [loc.addressLine1, loc.addressLine2].filter(Boolean).join(', ')
        const landMark = loc.landMark ? `Area- ${loc.landMark}` : ''
        const town = loc.city ? `Town- ${loc.city}` : ''
        const pin = loc.pinCode ? `PIN Code- ${loc.pinCode}` : ''
        const branchParts = [street, landMark, town, pin].filter(Boolean).join(', ')
        branchAddressStr = `Branch Address: ${branchName}${branchParts ? ', ' + branchParts : ''}`
      } else {
        const branchName = this.branchName || ''
        branchAddressStr = branchName ? `Branch Address: ${branchName}` : ''
      }

      const cinStr = 'CIN : U67100MH2017PTC292183'

      return [regOffice, branchAddressStr, cinStr].filter(Boolean).join('\n')
    }
  },
  methods: {
    getAddress(person) {
      if (!person) return 'N/A'
      const addr = person.customerAddress && person.customerAddress.length > 0
        ? person.customerAddress[0]
        : person
      return getFullAddress(addr)
    },
    getMobile(person) {
      if (!person) return 'N/A'
      return person.customerPhoneNumber || (Array.isArray(person.customerMobile) ? person.customerMobile[0] : 'N/A')
    },
    formatDate(date) {
      return date ? this.$dayjs(date).format('DD-MM-YYYY') : '-'
    },
    formatCurrency(val) {
      return Number(val || 0).toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },
    async generatePDF() {
      try {
        const primaryColor = '#00A1E4'
        const borderColor = '#00A1E4'
        const headerBg = null
        const headerTextColor = '#00A1E4'
        const dividerColor = '#00A1E4'
        const totalTextColor = '#00A1E4'
        const totalFillColor = null

        const docDefinition = {
          pageSize: 'A4',
          pageMargins: [35, 40, 35, 40],
          defaultStyle: {
            font: 'Roboto'
          },
          content: [
            // Top Border Line
            {
              canvas: [{ type: 'line', x1: 0, y1: 0, x2: 525, y2: 0, lineWidth: 0.5, strokeColor: primaryColor }],
              margin: [0, 0, 0, 4]
            },
            // Top Organization Header Columns
            {
              columns: [
                {
                  stack: [
                    { text: this.computedOrgName, style: 'orgHeaderName' },
                    { text: `Statement of Account of Loan Number ${this.selectedCase?.caseNo || ''}`, style: 'orgHeaderSub' }
                  ],
                  width: '60%'
                },
                {
                  text: this.currentDateTime,
                  alignment: 'right',
                  fontSize: 9,
                  color: '#4b5563',
                  width: '40%'
                }
              ]
            },
            // Line Divider below header
            {
              canvas: [{ type: 'line', x1: 0, y1: 0, x2: 525, y2: 0, lineWidth: 0.5, strokeColor: dividerColor }],
              margin: [0, 4, 0, 8]
            },
            // Address Details centered
            {
              stack: [
                { text: this.computedOrgName, style: 'orgTitleCentered' },
                {
                  text: this.computedOrgAddressText,
                  style: 'orgAddressText'
                }
              ],
              margin: [0, 0, 0, 10]
            },
            // Divider below address
            {
              canvas: [{ type: 'line', x1: 0, y1: 0, x2: 525, y2: 0, lineWidth: 0.5, strokeColor: dividerColor }],
              margin: [0, 0, 0, 12]
            },
            // Title block
            {
              table: {
                widths: ['100%'],
                body: [
                  [
                    { text: 'Statement Of Account', alignment: 'center', color: primaryColor, fillColor: headerBg, fontSize: 13, bold: true, margin: [0, 3, 0, 3] }
                  ]
                ]
              },
              layout: {
                hLineWidth: (i) => i === 0 || i === 1 ? 0.5 : 0,
                vLineWidth: () => 0,
                hLineColor: () => borderColor,
                vLineColor: () => borderColor
              },
              margin: [0, 0, 0, 12]
            },
            // I - Summary of Loan Header
            { text: 'I - Summary of Loan', style: 'sectionHeader' },
            // Summary details Grid
            {
              table: {
                widths: ['22%', '28%', '22%', '28%'],
                body: [
                  [
                    { text: 'Loan Account No', style: 'summaryLabel' },
                    { text: `: ${this.selectedCase?.caseNo || ''}`, style: 'summaryValue' },
                    { text: 'Loan Amount', style: 'summaryLabel' },
                    { text: `: ${this.formatCurrency(this.selectedCase?.loanDetails?.loanAmountSanctioned)}`, style: 'summaryValue' }
                  ],
                  [
                    { text: 'Branch', style: 'summaryLabel' },
                    { text: `: ${this.branchName || ''}`, style: 'summaryValue' },
                    { text: 'Mode Of Repayment', style: 'summaryLabel' },
                    { text: ': NACH', style: 'summaryValue' }
                  ],
                  [
                    { text: 'Product Type', style: 'summaryLabel' },
                    { text: `: ${this.selectedCase?.loanDetails?.caseType || 'Personal Loan'}`, style: 'summaryValue' },
                    { text: 'Loan Type', style: 'summaryLabel' },
                    { text: `: ${this.selectedCase?.loanDetails?.loanType || 'UnSecured Loan'}`, style: 'summaryValue' }
                  ]
                ]
              },
              layout: 'noBorders',
              margin: [0, 0, 0, 12]
            },
            // Customer Details Header
            { text: 'Customer Details', style: 'sectionHeader' },
            // Customer Details Table
            {
              table: {
                widths: ['15%', '12%', '23%', '35%', '15%'],
                headerRows: 1,
                body: [
                  [
                    { text: 'CIF', style: 'tableHeader', fillColor: headerBg },
                    { text: 'Type', style: 'tableHeader', fillColor: headerBg },
                    { text: 'Name', style: 'tableHeader', fillColor: headerBg },
                    { text: 'Address', style: 'tableHeader', fillColor: headerBg },
                    { text: 'Mobile No', style: 'tableHeader', fillColor: headerBg }
                  ],
                  [
                    { text: this.selectedCase?.customer?.crn || '-', style: 'tableCell' },
                    { text: 'Borrower', style: 'tableCell' },
                    { text: this.selectedCase?.customer?.customerFullName || '', style: 'tableCell' },
                    { text: this.borrowerAddress, style: 'tableCell' },
                    { text: this.borrowerMobile, style: 'tableCell' }
                  ],
                  ...this.guarantors.map(g => [
                    { text: g.crn || '-', style: 'tableCell' },
                    { text: 'Guarantor', style: 'tableCell' },
                    { text: g.customerFullName || g.name || '', style: 'tableCell' },
                    { text: this.getAddress(g), style: 'tableCell' },
                    { text: this.getMobile(g), style: 'tableCell' }
                  ])
                ]
              },
              layout: {
                hLineWidth: () => 0.5,
                vLineWidth: () => 0.5,
                hLineColor: () => borderColor,
                vLineColor: () => borderColor,
                paddingLeft: () => 6,
                paddingRight: () => 6,
                paddingTop: () => 5,
                paddingBottom: () => 5
              },
              margin: [0, 0, 0, 12]
            },
            // Ledger Details Header
            { text: 'Ledger Details', style: 'sectionHeader' },
            // Ledger Details Table
            {
              table: {
                widths: ['10%', '24%', '13%', '11%', '10%', '8%', '8%', '8%', '8%'],
                headerRows: 1,
                body: [
                  [
                    { text: 'Date', style: 'ledgerHeader', fillColor: headerBg },
                    { text: 'Particulars', style: 'ledgerHeader', fillColor: headerBg },
                    { text: 'Voucher Type', style: 'ledgerHeader', fillColor: headerBg },
                    { text: 'Payment Mode', style: 'ledgerHeader', fillColor: headerBg },
                    { text: 'Voucher No', style: 'ledgerHeader', fillColor: headerBg },
                    { text: 'Ins No', style: 'ledgerHeader', fillColor: headerBg },
                    { text: 'Debit', style: 'ledgerHeaderRight', fillColor: headerBg },
                    { text: 'Credit', style: 'ledgerHeaderRight', fillColor: headerBg },
                    { text: 'Balance', style: 'ledgerHeaderRight', fillColor: headerBg }
                  ],
                  ...this.ledgerEntries.map(entry => [
                    { text: this.formatDate(entry.date), style: 'ledgerCell' },
                    { text: entry.particulars || '', style: 'ledgerCell' },
                    { text: `${entry.voucherType || 'Journal'} Voucher`, style: 'ledgerCell' },
                    { text: entry.postingType === 'Auto' ? 'Journal' : 'Cash/Bank', style: 'ledgerCell' },
                    { text: entry.voucherNo || '', style: 'ledgerCell' },
                    { text: entry.instrumentNo || '', style: 'ledgerCell' },
                    { text: entry.debit > 0 ? this.formatCurrency(entry.debit) : '0.00', style: 'ledgerCellRight' },
                    { text: entry.credit > 0 ? this.formatCurrency(entry.credit) : '0.00', style: 'ledgerCellRight' },
                    { text: this.formatCurrency(entry.balance), style: 'ledgerCellRightBold' }
                  ]),
                  [
                    { text: 'TOTAL', style: 'ledgerTotalHeader', colSpan: 6, fillColor: totalFillColor },
                    {}, {}, {}, {}, {},
                    { text: this.formatCurrency(this.totalDebit), style: 'ledgerTotalRight', fillColor: totalFillColor },
                    { text: this.formatCurrency(this.totalCredit), style: 'ledgerTotalRight', fillColor: totalFillColor },
                    { text: this.formatCurrency(this.finalBalance), style: 'ledgerTotalRight', fillColor: totalFillColor }
                  ]
                ]
              },
              layout: {
                hLineWidth: () => 0.5,
                vLineWidth: () => 0.5,
                hLineColor: () => borderColor,
                vLineColor: () => borderColor,
                paddingLeft: () => 5,
                paddingRight: () => 5,
                paddingTop: () => 5,
                paddingBottom: () => 5
              },
              margin: [0, 0, 0, 12]
            },
            // Divider
            {
              canvas: [{ type: 'line', x1: 0, y1: 0, x2: 525, y2: 0, lineWidth: 0.5, strokeColor: dividerColor }],
              margin: [0, 8, 0, 4]
            },
            // Disclaimer message
            {
              text: 'Please quote your Loan Number when you contact us. For further clarifications please contact us on 7122520007 or info@zavronfinserv.com',
              alignment: 'center',
              fontSize: 8.5,
              color: '#4b5563',
              lineHeight: 1.3
            }
          ],
          styles: {
            orgHeaderName: {
              fontSize: 16,
              bold: true,
              color: primaryColor
            },
            orgHeaderSub: {
              fontSize: 9,
              italics: true,
              color: '#4b5563'
            },
            orgTitleCentered: {
              fontSize: 11,
              bold: true,
              alignment: 'center',
              color: '#111827'
            },
            orgAddressText: {
              fontSize: 8,
              alignment: 'center',
              color: '#4b5563',
              lineHeight: 1.3
            },
            sectionHeader: {
              fontSize: 11,
              bold: true,
              color: primaryColor,
              margin: [0, 8, 0, 4]
            },
            tableHeader: {
              fontSize: 9,
              bold: true,
              color: headerTextColor
            },
            tableCell: {
              fontSize: 8,
              color: '#111827'
            },
            summaryLabel: {
              fontSize: 9,
              bold: true,
              color: '#374151'
            },
            summaryValue: {
              fontSize: 9,
              color: '#111827'
            },
            ledgerHeader: {
              fontSize: 8,
              bold: true,
              color: headerTextColor
            },
            ledgerHeaderRight: {
              fontSize: 8,
              bold: true,
              color: headerTextColor,
              alignment: 'right'
            },
            ledgerCell: {
              fontSize: 7.5,
              color: '#111827'
            },
            ledgerCellRight: {
              fontSize: 7.5,
              color: '#111827',
              alignment: 'right'
            },
            ledgerCellRightBold: {
              fontSize: 7.5,
              bold: true,
              color: '#111827',
              alignment: 'right'
            },
            ledgerTotalHeader: {
              fontSize: 8,
              bold: true,
              color: totalTextColor,
              alignment: 'right'
            },
            ledgerTotalRight: {
              fontSize: 8,
              bold: true,
              color: totalTextColor,
              alignment: 'right'
            }
          }
        }

        const filename = `Ledger_Corporate_${this.selectedCase?.caseNo || 'Statement'}.pdf`
        pdfMake.createPdf(docDefinition).download(filename)
      } catch (error) {
        console.error('PDFMake generation failed:', error)
        this.$toast.error('Failed to generate PDF document')
      }
    }
  }
}
</script>
